import { isAxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useSWR from 'swr';
import { toast } from 'App';
import { UpdatePermissionGate } from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { invoicePaymentsService } from 'views/services/invoicePaymentsService';
import { inputCls, labelCls, primaryBtn } from './shared';

interface IFormValues {
  min_amount_eur: string;
  max_amount_eur: string;
  agreement_threshold_eur: string;
  sof_threshold_eur: string;
  quote_validity_seconds: number;
  inactivity_timeout_seconds: number;
}

export const SettingsPanel = () => {
  const [saving, setSaving] = useState(false);
  const { data, mutate } = useSWR(
    '/api/admin/invoice-payment-config/settings',
    () => invoicePaymentsService.getSettings()
  );
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormValues>({ mode: 'onChange' });

  useEffect(() => {
    if (data) {
      reset({
        min_amount_eur: data.min_amount_eur,
        max_amount_eur: data.max_amount_eur,
        agreement_threshold_eur: data.agreement_threshold_eur,
        sof_threshold_eur: data.sof_threshold_eur,
        quote_validity_seconds: data.quote_validity_seconds,
        inactivity_timeout_seconds: data.inactivity_timeout_seconds,
      });
    }
  }, [data, reset]);

  const onSubmit = async (values: IFormValues) => {
    try {
      setSaving(true);
      const updated = await invoicePaymentsService.updateSettings({
        ...values,
        quote_validity_seconds: Number(values.quote_validity_seconds),
        inactivity_timeout_seconds: Number(values.inactivity_timeout_seconds),
      });
      await mutate(updated, { revalidate: false });
      toast({ title: 'Settings updated', status: 'success' });
    } catch (error) {
      const message = isAxiosError(error)
        ? (error.response?.data?.message ?? error.message)
        : 'Failed to update settings';
      toast({
        title: 'Update failed',
        description: String(message),
        status: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const numberField = (
    name: keyof IFormValues,
    label: string,
    hint: string
  ) => (
    <div>
      <label className={labelCls}>{label}</label>
      <input
        className={inputCls}
        {...register(name, { required: 'Required' })}
      />
      <div className="mt-1 text-xs text-gray-500">{hint}</div>
      {errors[name] && (
        <div className="text-xs text-red-500">
          {String(errors[name]?.message)}
        </div>
      )}
    </div>
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {numberField(
          'min_amount_eur',
          'Minimum amount (EUR equivalent)',
          'Requests below this are rejected at quote time'
        )}
        {numberField(
          'max_amount_eur',
          'Maximum amount (EUR equivalent)',
          'Requests above this are rejected at quote time'
        )}
        {numberField(
          'agreement_threshold_eur',
          'Agreement/PO threshold (EUR)',
          'Above this an agreement or purchase order is mandatory'
        )}
        {numberField(
          'sof_threshold_eur',
          'Source of Funds threshold (EUR)',
          'Above this a Source of Funds document is mandatory'
        )}
        {numberField(
          'quote_validity_seconds',
          'Quote validity (seconds)',
          'How long a quote stays locked (default 300)'
        )}
        {numberField(
          'inactivity_timeout_seconds',
          'Flow inactivity timeout (seconds)',
          'Webapp discards the draft after this idle time (default 1800)'
        )}
      </div>
      <UpdatePermissionGate resource={PERMISSION_RESOURCES.INVOICE_CONFIG}>
        <button
          type="submit"
          disabled={saving}
          className={`mt-4 ${primaryBtn}`}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </UpdatePermissionGate>
    </form>
  );
};
